/**
 * bindings.ts — the TS mirror of backend/projects/bindings.py.
 *
 * A ParamSpec (or an edge's implicit `weight` param, via `GraphEdge
 * .weightBinding`) may declare a `CellBinding` {table, keyColumn, key,
 * column}. When bound, the project DB cell is the source of truth for the
 * param's live value; the saved `value` is only a fallback when the row/cell
 * is missing, empty or non-numeric.
 *
 * Two consumers:
 * - `LocalAdapter` merges a resolution into its per-graph param overlays each
 *   tick (the in-browser engine's half of "DB writes drive the graph"; in
 *   relay mode the BACKEND resolves before broadcast instead).
 * - The node/edge modals + knob rows call `resolveCellBindings` directly to
 *   render the bound value and the broken-binding badge.
 *
 * Snapshots come from the tables poll (GET /api/projects/{id}/tables) and are
 * row-capped — a bound row beyond the cap resolves as broken (fallback) until
 * the backend resolution path takes over in relay mode. Pure functions: no
 * adapter state, safe to call during render.
 */

import type { CellBinding, ScenarioBundle } from './types';
import type { ResolvedParams } from './compute';

/** One table as served by the tables snapshot (useSim.tablesState entries /
 *  KnobTableSnapshot). Rows are `{id, cells}` with untyped cell values. */
export interface BindingTableSnapshot {
  name: string;
  columns: { name: string; type?: string }[];
  rows: { id: number | string; cells: Record<string, unknown> }[];
  count?: number;
}

/** One bound param found on a bundle — the walk-order counterpart of the
 *  backend's `collect_bindings`. `lo`/`hi` clamp the resolved value to the
 *  ParamSpec's declared range (the implicit edge weight clamps to ≥ 0 only:
 *  bound weights may exceed 1 because fan-out normalises). */
export interface BoundParam {
  path: string;
  binding: CellBinding;
  graphId: string;
  lo?: number;
  hi?: number;
}

/** Index every cell binding across a project's graph collection, keyed by
 *  setParam path (`<nodeId>.<key>` / `e:<edgeId>.<key>`). Node params, gate
 *  params and source distParams share one path namespace per node, mirroring
 *  `resolveInitialParams`. */
export function collectBindings(bundles: ScenarioBundle[]): Map<string, BoundParam> {
  const out = new Map<string, BoundParam>();
  for (const bundle of bundles) {
    for (const n of bundle.graph.nodes) {
      const specs = [...n.params, ...(n.gate?.params ?? []), ...(n.source?.distParams ?? [])];
      for (const spec of specs) {
        if (spec.binding) {
          const path = `${n.id}.${spec.key}`;
          out.set(path, { path, binding: spec.binding, graphId: bundle.id, lo: spec.min, hi: spec.max });
        }
      }
    }
    for (const e of bundle.graph.edges) {
      if (e.weightBinding) {
        const path = `e:${e.id}.weight`;
        out.set(path, { path, binding: e.weightBinding, graphId: bundle.id, lo: 0 });
      }
      for (const spec of e.params) {
        if (spec.binding) {
          const path = `e:${e.id}.${spec.key}`;
          out.set(path, { path, binding: spec.binding, graphId: bundle.id, lo: spec.min, hi: spec.max });
        }
      }
    }
  }
  return out;
}

/** True when any bundle declares at least one cell binding — the cheap check
 *  that keeps the tables poll alive (bound cells are model/agent-mutated even
 *  while the Data panel is closed, unlike db knob pins). */
export function hasBindings(bundles: ScenarioBundle[]): boolean {
  for (const b of bundles) {
    for (const n of b.graph.nodes) {
      if (n.params.some((p) => p.binding)) return true;
      if (n.gate?.params.some((p) => p.binding)) return true;
      if (n.source?.distParams?.some((p) => p.binding)) return true;
    }
    if (b.graph.edges.some((e) => e.weightBinding || e.params.some((p) => p.binding))) return true;
  }
  return false;
}

function toNumber(raw: unknown): number | null {
  if (typeof raw === 'boolean') return null;
  if (typeof raw === 'number') return Number.isFinite(raw) ? raw : null;
  if (typeof raw === 'string') {
    const v = Number(raw.trim());
    return Number.isFinite(v) ? v : null;
  }
  return null;
}

function clamp(value: number, lo?: number, hi?: number): number {
  if (lo !== undefined && value < lo) return lo;
  if (hi !== undefined && value > hi) return hi;
  return value;
}

/** Coerce a snapshot cell to the string form compared against a binding's
 *  `key` (the backend's filter language compares string equality; text key
 *  columns are the designed use, numeric cells coerce permissively). */
function keyOf(cell: unknown): string {
  return cell === null || cell === undefined ? '' : String(cell);
}

export interface CellBindingResolution {
  /** path → resolved (clamped) DB value. Only successfully resolved paths
   *  appear; absent paths keep the saved ParamSpec default (fallback). */
  values: Record<string, number>;
  /** Paths whose row/cell is missing, empty or non-numeric — the UI's
   *  broken-binding badge; the saved default is in effect for these. */
  broken: Set<string>;
  /** Per-graph sparse param overlays to merge into the runtime maps:
   *  `paramsByGraph[graphId].nodes[nodeId][key] = value`. */
  patches: Record<string, ResolvedParams>;
}

/** Resolve every binding against the table snapshots. Returns null when the
 *  project declares no bindings (the common fast path). */
export function resolveCellBindings(
  bundles: ScenarioBundle[],
  snapshots: readonly BindingTableSnapshot[] | null | undefined,
): CellBindingResolution | null {
  const bindings = collectBindings(bundles);
  if (bindings.size === 0) return null;
  const byTable = new Map<string, BindingTableSnapshot>();
  for (const t of snapshots ?? []) byTable.set(t.name, t);

  const res: CellBindingResolution = { values: {}, broken: new Set(), patches: {} };
  // undefined = table absent from the snapshot; null = table present but no
  // matching row; an object = the matched row's cells.
  const rowCache = new Map<string, Record<string, unknown> | null | undefined>();
  for (const bp of bindings.values()) {
    const { table, keyColumn, key, column } = bp.binding;
    const ck = `${table}${keyColumn}=${key}`;
    if (!rowCache.has(ck)) {
      const t = byTable.get(table);
      const row = t?.rows.find((r) => keyOf(r.cells[keyColumn]) === key);
      rowCache.set(ck, row ? row.cells : t ? null : undefined);
    }
    const cached = rowCache.get(ck);
    if (cached === undefined) {
      res.broken.add(bp.path); // table not in the snapshot (or not loaded)
      continue;
    }
    if (cached === null) {
      res.broken.add(bp.path); // table present, no matching row
      continue;
    }
    const value = toNumber(cached[column]);
    if (value === null) {
      res.broken.add(bp.path); // missing/empty/non-numeric cell
      continue;
    }
    const clamped = clamp(value, bp.lo, bp.hi);
    res.values[bp.path] = clamped;
    const patch = (res.patches[bp.graphId] ??= { nodes: {}, edges: {} });
    if (bp.path.startsWith('e:')) {
      const rest = bp.path.slice(2); // <edgeId>.<paramKey>
      const dot = rest.indexOf('.');
      (patch.edges[rest.slice(0, dot)] ??= {})[rest.slice(dot + 1)] = clamped;
    } else {
      const dot = bp.path.indexOf('.'); // <nodeId>.<paramKey>
      (patch.nodes[bp.path.slice(0, dot)] ??= {})[bp.path.slice(dot + 1)] = clamped;
    }
  }
  return res;
}

/** Merge a resolution's patches into a runtime param map (mutates + returns
 *  it). Bound paths overwrite whatever setParam last wrote — the DB is the
 *  source of truth; slider writes PATCH the cell and the refreshed snapshot
 *  flows back through here. */
export function applyBindingPatches(
  params: ResolvedParams,
  patches: Record<string, ResolvedParams>,
): void {
  for (const per of Object.values(patches)) {
    for (const [nid, kv] of Object.entries(per.nodes)) {
      params.nodes[nid] = { ...(params.nodes[nid] ?? {}), ...kv };
    }
    for (const [eid, kv] of Object.entries(per.edges)) {
      params.edges[eid] = { ...(params.edges[eid] ?? {}), ...kv };
    }
  }
}

/** One binding's cell lookup for UI display + write-through.
 *  - `table: null`  → the table isn't in the snapshot (not loaded / deleted)
 *  - `rowId: null`  → no row matches the key-column lookup
 *  - `value: null`  → the cell is missing/empty/non-numeric
 *  Any null ⇒ broken (the saved ParamSpec default is in effect). */
export interface BoundCellLookup {
  table: BindingTableSnapshot | null;
  rowId: number | string | null;
  value: number | null;
}

/** Look up one binding's row + cell in the tables snapshot. Pure; cheap. */
export function lookupBoundCell(
  snapshots: readonly BindingTableSnapshot[] | null | undefined,
  binding: CellBinding,
): BoundCellLookup {
  const table = snapshots?.find((t) => t.name === binding.table) ?? null;
  if (!table) return { table: null, rowId: null, value: null };
  const row = table.rows.find((r) => keyOf(r.cells[binding.keyColumn]) === binding.key);
  if (!row) return { table, rowId: null, value: null };
  return { table, rowId: row.id, value: toNumber(row.cells[binding.column]) };
}

/** Compact human label for a binding: `media[name=instagram].budget`. */
export function bindingLabel(binding: CellBinding): string {
  return `${binding.table}[${binding.keyColumn}=${binding.key}].${binding.column}`;
}
